import { useEffect, useMemo, useState, type FormEvent, type ReactElement } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { AGENT_CONVERSATIONS_URL, DEFAULT_LIST_PAGE_SIZE, agentQuoteDecisionUrl } from '../../constants';
import type { AgentCitation, AgentConversation, AgentMessage } from '../../domain/types';
import { ActivityTable } from '../activity/ActivityTable';
import { GroupedDataGrid, type GroupedColumnDef } from '../grid/GroupedDataGrid';
import { GridPaginationBar } from '../grids/GridPaginationBar';
import { FilterTiles } from '../master/FilterTiles';

type AuthFetch = (url: string, init?: RequestInit) => Promise<Response>;

const COLUMNS: GroupedColumnDef[] = [
  { id: 'title', label: 'Title', header: 'Title' },
  { id: 'status', label: 'Status', header: 'Status' },
  { id: 'region', label: 'Region', header: 'Region' },
  { id: 'updated', label: 'Updated', header: 'Updated' }
];

function groupValue(row: AgentConversation, columnId: string): string {
  switch (columnId) {
    case 'title':
      return row.title || '—';
    case 'status':
      return row.status || '—';
    case 'region':
      return row.region || '—';
    case 'updated':
      return row.updatedAtUtc || '—';
    default:
      return '—';
  }
}

function statusLabel(status: string): string {
  const raw = (status || 'Open').trim();
  return raw.charAt(0).toUpperCase() + raw.slice(1);
}

export function AssistantRegister({ authFetch }: { authFetch: AuthFetch }): ReactElement {
  const navigate = useNavigate();
  const { conversationId } = useParams();
  const isFullPage = Boolean(conversationId);
  const [rows, setRows] = useState<AgentConversation[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_LIST_PAGE_SIZE);
  const [selectedId, setSelectedId] = useState<string | null>(conversationId ?? null);
  const [detail, setDetail] = useState<AgentConversation | null>(null);
  const [title, setTitle] = useState('');
  const [question, setQuestion] = useState('');
  const [sending, setSending] = useState(false);
  const [requestId, setRequestId] = useState('');
  const [drafting, setDrafting] = useState(false);

  async function reloadList(): Promise<void> {
    setLoading(true);
    try {
      const res = await authFetch(AGENT_CONVERSATIONS_URL);
      if (!res.ok) {
        setError(res.status === 403 ? 'You do not have permission to open Assistant.' : 'Could not load conversations.');
        setRows([]);
        return;
      }
      const body = (await res.json()) as { items?: AgentConversation[] };
      setRows(Array.isArray(body.items) ? body.items : []);
      setError('');
    } catch {
      setError('Could not load conversations.');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void reloadList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authFetch]);

  useEffect(() => {
    if (conversationId) {
      setSelectedId(conversationId);
    }
  }, [conversationId]);

  useEffect(() => {
    if (!selectedId) {
      setDetail(null);
      return;
    }
    const cached = rows.find((row) => row.id === selectedId);
    if (cached) {
      setDetail(cached);
    }
    let cancelled = false;
    void (async () => {
      const res = await authFetch(`${AGENT_CONVERSATIONS_URL}/${encodeURIComponent(selectedId)}`);
      if (!res.ok || cancelled) {
        return;
      }
      setDetail((await res.json()) as AgentConversation);
    })();
    return () => {
      cancelled = true;
    };
  }, [authFetch, selectedId, rows]);

  const filtered = useMemo(() => {
    if (statusFilter === 'all') {
      return rows;
    }
    return rows.filter((row) => row.status.toLowerCase() === statusFilter);
  }, [rows, statusFilter]);

  const paged = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, page, pageSize]);

  const tileOptions = useMemo(() => {
    const open = rows.filter((r) => r.status.toLowerCase() === 'open').length;
    const answered = rows.filter((r) => r.status.toLowerCase() === 'answered').length;
    const empty = rows.filter((r) => r.status.toLowerCase() === 'empty').length;
    return [
      { id: 'all', label: 'All', count: rows.length },
      { id: 'open', label: 'Open', count: open },
      { id: 'answered', label: 'Answered', count: answered },
      { id: 'empty', label: 'Empty', count: empty }
    ];
  }, [rows]);

  async function onCreate(e: FormEvent): Promise<void> {
    e.preventDefault();
    const res = await authFetch(AGENT_CONVERSATIONS_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ title: title.trim() || 'New conversation' })
    });
    if (!res.ok) {
      setError(res.status === 403 ? 'You do not have permission to open Assistant.' : 'Could not start a conversation.');
      return;
    }
    const created = (await res.json()) as AgentConversation;
    setTitle('');
    setRows((prev) => [created, ...prev.filter((row) => row.id !== created.id)]);
    setSelectedId(created.id);
    setDetail(created);
  }

  async function onAsk(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (!selectedId || !question.trim()) {
      return;
    }
    setSending(true);
    try {
      const res = await authFetch(`${AGENT_CONVERSATIONS_URL}/${encodeURIComponent(selectedId)}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ question: question.trim() })
      });
      if (res.status === 503) {
        const body = (await res.json().catch(() => ({}))) as { message?: string };
        setError(body.message || 'Foundry chat is not configured.');
        setQuestion('');
        await reloadList();
        return;
      }
      if (!res.ok) {
        setError(res.status === 404 ? 'Conversation not found.' : 'Could not send the question.');
        return;
      }
      const updated = (await res.json()) as AgentConversation;
      setDetail(updated);
      setRows((prev) => prev.map((row) => (row.id === updated.id ? updated : row)));
      setQuestion('');
      setError('');
    } finally {
      setSending(false);
    }
  }

  async function onDraft(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (!selectedId || !requestId.trim()) {
      return;
    }
    setDrafting(true);
    try {
      const res = await authFetch(`${AGENT_CONVERSATIONS_URL}/${encodeURIComponent(selectedId)}/quote-drafts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ procurementRequestId: requestId.trim() })
      });
      if (!res.ok) {
        const text = await res.text();
        setError(text || 'Could not draft quotes.');
        return;
      }
      const updated = (await res.json()) as AgentConversation;
      setDetail(updated);
      setRows((prev) => prev.map((row) => (row.id === updated.id ? updated : row)));
      setError('');
    } finally {
      setDrafting(false);
    }
  }

  async function onDecision(decision: 'Approve' | 'Reject'): Promise<void> {
    if (!detail?.quoteDraft?.rfqId) {
      return;
    }
    const res = await authFetch(agentQuoteDecisionUrl(detail.quoteDraft.rfqId), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ decision })
    });
    if (!res.ok) {
      const text = await res.text();
      setError(text || 'Could not record the decision.');
      return;
    }
    const updated = (await res.json()) as AgentConversation;
    setDetail(updated);
    setRows((prev) => prev.map((row) => (row.id === updated.id ? updated : row)));
    setError('');
  }

  function renderRow(row: AgentConversation): ReactElement {
    return (
      <tr
        className={selectedId === row.id ? 'selected' : undefined}
        onClick={() => setSelectedId(row.id)}
        onDoubleClick={(e) => {
          e.preventDefault();
          navigate(`/assistant/${encodeURIComponent(row.id)}`);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setSelectedId(row.id);
          }
        }}
        role="button"
        tabIndex={0}
      >
        <td>{row.title}</td>
        <td>
          <span className={`status-pill status-${row.status.toLowerCase()}`}>{statusLabel(row.status)}</span>
        </td>
        <td className="mono-id">{row.region}</td>
        <td>{row.updatedAtUtc ? new Date(row.updatedAtUtc).toLocaleString() : '—'}</td>
      </tr>
    );
  }

  const panel = detail ? (
    <AssistantThreadPanel
      conversation={detail}
      variant={isFullPage ? 'page' : 'panel'}
      question={question}
      sending={sending}
      onQuestion={setQuestion}
      onAsk={onAsk}
      requestId={requestId}
      drafting={drafting}
      onRequestId={setRequestId}
      onDraft={onDraft}
      onDecision={onDecision}
      onClose={() => setSelectedId(null)}
      onBack={() => {
        setSelectedId(null);
        navigate('/assistant');
      }}
    />
  ) : null;

  if (isFullPage) {
    return (
      <div className="detail-page-shell">
        {error && <p className="error">{error}</p>}
        {panel}
      </div>
    );
  }

  return (
    <div className={`requests-shell${selectedId ? ' has-panel' : ''}`}>
      <FilterTiles
        testId="assistant-tiles"
        value={statusFilter}
        onChange={(next) => {
          setStatusFilter(next);
          setPage(1);
        }}
        options={tileOptions}
      />
      <section className="card requests-grid-card" data-testid="assistant-grid">
        <div className="row spread requests-grid-toolbar">
          <h2>Assistant</h2>
        </div>
        <p className="hint">
          Click a row for the side panel; double-click to open the full page. Drag the grip (⠿) beside a title into the
          strip to group.
        </p>
        <form className="grid" data-testid="assistant-create-form" onSubmit={(e) => void onCreate(e)}>
          <label>
            Title
            <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Last paid casing in Permian" />
          </label>
          <button type="submit">Start conversation</button>
        </form>
        {loading && <p className="hint">Loading conversations…</p>}
        {error && <p className="error">{error}</p>}
        <GroupedDataGrid<AgentConversation>
          columns={COLUMNS}
          rows={paged}
          getRowId={(r) => r.id}
          getGroupValue={groupValue}
          renderRow={renderRow}
          colSpan={4}
          emptyMessage="No conversations match this status."
        />
        <GridPaginationBar
          idPrefix="assistant-grid"
          page={page}
          pageSize={pageSize}
          totalCount={filtered.length}
          onPageChange={setPage}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(1);
          }}
        />
      </section>
      {panel}
    </div>
  );
}

function AssistantThreadPanel({
  conversation,
  variant,
  question,
  sending,
  onQuestion,
  onAsk,
  requestId,
  drafting,
  onRequestId,
  onDraft,
  onDecision,
  onClose,
  onBack
}: {
  conversation: AgentConversation;
  variant: 'panel' | 'page';
  question: string;
  sending: boolean;
  onQuestion: (value: string) => void;
  onAsk: (e: FormEvent) => void;
  requestId: string;
  drafting: boolean;
  onRequestId: (value: string) => void;
  onDraft: (e: FormEvent) => void;
  onDecision: (decision: 'Approve' | 'Reject') => void;
  onClose: () => void;
  onBack: () => void;
}): ReactElement {
  const lastAssistant = [...conversation.messages].reverse().find((m) => m.role === 'assistant');
  return (
    <aside
      className={`side-panel${variant === 'page' ? ' side-panel--page' : ''}`}
      aria-label="Assistant details"
      data-testid="assistant-side-panel"
    >
      <div className="side-panel-header">
        <div className="side-panel-title-row">
          {variant === 'page' && (
            <button type="button" className="secondary panel-back-btn" onClick={onBack}>
              Back to list
            </button>
          )}
          <h3>Assistant {conversation.title}</h3>
        </div>
        <div className="side-panel-actions">
          {variant === 'panel' && (
            <button type="button" className="icon-close" onClick={onClose} aria-label="Close panel">
              ×
            </button>
          )}
        </div>
      </div>
      <div className="side-panel-body">
        <dl className="detail-list">
          <dt>Title</dt>
          <dd>{conversation.title}</dd>
          <dt>Status</dt>
          <dd>
            <span className={`status-pill status-${conversation.status.toLowerCase()}`}>
              {statusLabel(conversation.status)}
            </span>
          </dd>
          <dt>Region</dt>
          <dd className="mono-id">{conversation.region || '—'}</dd>
          <dt>Created by</dt>
          <dd>{conversation.createdBy || '—'}</dd>
        </dl>
        <section className="nested-card" aria-label="Thread">
          <h4 className="form-section-title">Thread</h4>
          {conversation.messages.length === 0 ? (
            <p className="hint">Ask a question about last paid prices in your region.</p>
          ) : (
            conversation.messages.map((message) => <ThreadMessage key={message.id} message={message} />)
          )}
        </section>
        {lastAssistant && lastAssistant.citations.length > 0 && (
          <section className="nested-card" aria-label="Citations">
            <h4 className="form-section-title">Citations</h4>
            <ul>
              {lastAssistant.citations.map((cite) => (
                <li key={`${cite.sourceType}-${cite.sourceId}`}>
                  <CitationLink cite={cite} />
                  {cite.vendorName ? ` · ${cite.vendorName}` : ''}
                  {cite.unitPrice != null ? ` · ${cite.unitPrice}` : ''}
                </li>
              ))}
            </ul>
          </section>
        )}
        <form className="grid" data-testid="assistant-composer" onSubmit={onAsk}>
          <label>
            Question
            <textarea value={question} onChange={(e) => onQuestion(e.target.value)} rows={3} />
          </label>
          <button type="submit" disabled={sending || !question.trim()}>
            {sending ? 'Sending…' : 'Send'}
          </button>
        </form>
        <form className="grid" data-testid="assistant-quote-form" onSubmit={onDraft}>
          <label>
            Procurement request
            <input value={requestId} onChange={(e) => onRequestId(e.target.value)} placeholder="Request id" />
          </label>
          <button type="submit" className="secondary" disabled={drafting || !requestId.trim()}>
            {drafting ? 'Drafting…' : 'Draft quotes'}
          </button>
        </form>
        {conversation.quoteDraft && (
          <section className="nested-card" data-testid="assistant-quote-review" aria-label="Quote draft">
            <h4 className="form-section-title">Quote draft</h4>
            <dl className="detail-list">
              <dt>Request</dt>
              <dd className="mono-id">{conversation.quoteDraft.requestNumber || '—'}</dd>
              <dt>RFQ</dt>
              <dd className="mono-id">{conversation.quoteDraft.rfqNumber || '—'}</dd>
              <dt>Status</dt>
              <dd>
                <span className="status-pill">{conversation.quoteDraft.status}</span>
              </dd>
              <dt>Materials</dt>
              <dd>{conversation.quoteDraft.materials.join(', ') || '—'}</dd>
              <dt>Vendors</dt>
              <dd>
                {conversation.quoteDraft.vendors.length === 0
                  ? '—'
                  : conversation.quoteDraft.vendors
                      .map((v) => `${v.name} <${v.email}>${v.citedPrice != null ? ` ${v.citedPrice}` : ''}`)
                      .join('; ')}
              </dd>
            </dl>
            {conversation.quoteDraft.status === 'Pending' && (
              <div className="panel-actions">
                <button type="button" onClick={() => onDecision('Approve')}>
                  Approve
                </button>
                <button type="button" className="secondary" onClick={() => onDecision('Reject')}>
                  Reject
                </button>
              </div>
            )}
          </section>
        )}
        <ActivityTable
          testId="assistant-activity"
          rows={conversation.activity.map((row) => ({
            id: row.id,
            actor: row.actor,
            occurredAtUtc: row.occurredAtUtc,
            action: row.action,
            description: row.description
          }))}
        />
      </div>
    </aside>
  );
}

function ThreadMessage({ message }: { message: AgentMessage }): ReactElement {
  return (
    <p>
      <strong>{message.role === 'user' ? 'You' : 'Assistant'}: </strong>
      {message.content}
    </p>
  );
}

const CITATION_LABEL: Record<string, string> = {
  purchaseOrder: 'Purchase order',
  request: 'Request',
  requestMaterial: 'Request',
  rfq: 'Quote request',
  rfqQuote: 'Quote request',
  vendor: 'Vendor',
  well: 'Well',
  inventory: 'Inventory',
  materialTransfer: 'Material transfer'
};

function citationHref(sourceType: string, sourceId: string): string | null {
  const id = sourceId.trim();
  if (!id) {
    return null;
  }
  const encoded = encodeURIComponent(id);
  switch (sourceType) {
    case 'purchaseOrder':
      return `/purchase-orders/${encoded}`;
    case 'request':
    case 'requestMaterial':
      return `/requests/${encoded}`;
    case 'rfq':
    case 'rfqQuote':
      return `/request-for-quotes/${encoded}`;
    case 'vendor':
      return '/vendors';
    case 'well':
      return '/wells';
    case 'inventory':
      return '/inventory';
    case 'materialTransfer':
      return `/material-transfers/${encoded}/edit`;
    default:
      return null;
  }
}

export function CitationLink({ cite }: { cite: AgentCitation }): ReactElement {
  const href = citationHref(cite.sourceType, cite.sourceId);
  const label = CITATION_LABEL[cite.sourceType] ?? cite.sourceType;
  const text = cite.title.trim() || label;
  if (!href) {
    return (
      <>
        {text} ({label})
      </>
    );
  }
  return (
    <Link to={href} title={label}>
      {text}
    </Link>
  );
}
